<template>
  <section class="section">
    <svg-icons />

    <h2 class="title">
      {{ $t('route.forwarders') }}
      <div class="level">
        <div class="level-left subtitle">
          {{ $t('subtitle.forwarders') }}
        </div>
        <div class="level-right">
          <button
            class="button is-primary"
            @click.prevent="handleShowFilter"
            :class="{ 'is-info': hasActiveFilters }"
          >
            <octicon :icon="icons.search" />
            <span>{{ $t('users.filter.title') }}</span>
            <span v-if="hasActiveFilters" class="tag is-light ml-1">{{ activeFilterCount }}</span>
          </button>
          <button class="button is-primary ml-2" @click.prevent="create">
            <octicon :icon="icons.plus" /><span>{{ $t('newForwarder') }}</span>
          </button>
          <agent-download-dropdown class="ml-2" />
        </div>
      </div>
    </h2>
    
    <forwarder-list ref="forwarderList" @edit="edit" :filters.sync="forwarderFilters" />
    
    <!-- Add/Edit Forwarder Modal (Multi-step) -->
    <add-forwarder-modal
      v-if="showAddForwarderModal"
      :show.sync="showAddForwarderModal"
      :index-names="indexNames"
      :windows-source-types="windowsSourceTypes"
      :linux-source-types="linuxSourceTypes"
      :resource-monitor-source-types="resourceMonitorSourceTypes"
      :file-log-source-types="fileLogSourceTypes"
      :is-edit-mode="isEditMode"
      :edit-item="editItem"
      @success="handleForwarderSuccess"
    />
    
    <!-- Forwarder Filter Modal -->
    <forwarder-filter
      :show.sync="showForwarderFilter"
      :filters="forwarderFilters"
      @apply-filters="handleApplyFilters"
    />
    <modal :show.sync="createVersionModal" class="dark-modal">
      <div class="modal-box">
        <div class="modal-box-header">
          <h2 class="modal-title">{{ $t('forwarders.uploadNewVersion') }}</h2>
        </div>
        <form ref="addVersionForm" @submit.prevent="handleUploadedAgents">
          <div class="os-upload-grid">
            <file-upload-section
              :title="$t('forwarders.windowsX64')"
              os-type="windows_x64"
              :selected-file="selectedFiles.windows_x64"
              :is-drag-over="dragStates.windows_x64"
              @file-selected="handleFileSelected"
              @drag-over="handleDragOver"
              @drag-enter="handleDragEnter"
              @drag-leave="handleDragLeave"
              @drop="handleDrop"
            />

            <file-upload-section
              :title="$t('forwarders.windowsX32')"
              os-type="windows_x32"
              :selected-file="selectedFiles.windows_x32"
              :is-drag-over="dragStates.windows_x32"
              @file-selected="handleFileSelected"
              @drag-over="handleDragOver"
              @drag-enter="handleDragEnter"
              @drag-leave="handleDragLeave"
              @drop="handleDrop"
            />

            <file-upload-section
              :title="$t('forwarders.linuxX64')"
              os-type="linux_x64"
              :selected-file="selectedFiles.linux_x64"
              :is-drag-over="dragStates.linux_x64"
              @file-selected="handleFileSelected"
              @drag-over="handleDragOver"
              @drag-enter="handleDragEnter"
              @drag-leave="handleDragLeave"
              @drop="handleDrop"
            />

            <file-upload-section
              :title="$t('forwarders.linuxX32')"
              os-type="linux_x32"
              :selected-file="selectedFiles.linux_x32"
              :is-drag-over="dragStates.linux_x32"
              @file-selected="handleFileSelected"
              @drag-over="handleDragOver"
              @drag-enter="handleDragEnter"
              @drag-leave="handleDragLeave"
              @drop="handleDrop"
            />
          </div>

          <div class="form-fields-container">
            <div class="form-column">
              <div class="field description-field">
                <label class="label">{{ $t('description') }}</label>
                <div class="control">
                  <textarea v-model="description" class="textarea" :placeholder="$t('forwarders.inputText')" maxlength="256" autocomplete="off"></textarea>
                  <div class="char-count">{{ description.length }}/256</div>
                </div>
              </div>
            </div>
          </div>

        </form>
        <div class="modal-footer">
          <button type="button" class="button cancel-button" @click="createVersionModal = false; resetForm()">
            {{ $t('cancel') }}
          </button>
          <button type="button" class="button is-primary" :disabled="isSavingVersion || !isFormValid()" :class="{ 'is-loading': isSavingVersion }" @click="handleUploadedAgents">
            {{ $t('complete') }}
          </button>
        </div>
      </div>
    </modal>
    <agent-list />
    <h2 class="title">
      {{ $t('version') }}
      <div class="level">
        <div class="level-left subtitle">
          {{ $t('forwarders.releasedVersions') }}
        </div>
        <div class="level-right">
          <button class="button is-primary" @click.prevent="createVersionModal = true">
            <octicon :icon="icons.plus" /> <span>{{ $t('forwarders.uploadNewVersion') }}</span>
          </button>
        </div>
      </div>
    </h2>
    <data-table ref="versionTable" :items="fetchVersions" :theme="$_ui_theme_tables">
      <data-column field="version" :label="$t('version')" width="10%" />
      <data-column field="release_date" :label="$t('forwarders.releaseDate')" width="15%">
        <template slot-scope="props">
          {{ props.value ? format(new Date(props.value), 'dd/MM/yyyy HH:mm:ss') : $t('none') }}
        </template>
      </data-column>
      <data-column field="description" :label="$t('description')" width="30%" />
      <data-column field="os" :label="$t('forwarders.os')" width="10%" />
      <data-column field="architecture" :label="$t('forwarders.architecture')" width="10%" />
      <data-column field="is_stable" :label="$t('forwarders.stable')" width="10%">
        <template slot-scope="props">
          <span v-if="props.value" class="tag is-success is-light">{{ $t('yes') }}</span>
          <span v-else class="tag is-warning is-light">{{ $t('no') }}</span>
        </template>
      </data-column>
      <data-column :label="$t('actions')" class="has-text-centered" :sortable="false" width="15%">
        <template slot-scope="props">
          <div class="has-text-centered">
            <button class="button is-text" @click.prevent="editVersion(props.item)">
              <octicon :icon="icons.pencil" /> <span>{{ $t('edit') }}</span>
            </button>
            <a :href="props.item.url" class="button is-text" download>
              <octicon :icon="icons.desktopDownload" /> <span>{{ $t('download') }}</span>
            </a>
          </div>
        </template>
      </data-column>
    </data-table>

    <modal v-if="versionItem" :show.sync="versionModal" class="dark-modal">
      <div class="modal-box">
        <div class="modal-box-header">
          <h2 class="modal-title">{{ $t('forwarders.editVersion') }}: {{ versionItem.version }}</h2>
        </div>
        <form @submit.prevent="updateVersion">
          <div class="field">
            <label class="label">{{ $t('version') }}</label>
            <div class="control">
              <input v-model="versionItem.version" class="input" type="text" disabled autocomplete="off">
            </div>
          </div>

          <div class="field">
            <label class="label">{{ $t('forwarders.os') }}</label>
            <div class="control">
              <input v-model="versionItem.os" class="input" type="text" disabled autocomplete="off">
            </div>
          </div>

          <div class="field">
            <label class="label">{{ $t('forwarders.architecture') }}</label>
            <div class="control">
              <input v-model="versionItem.architecture" class="input" type="text" disabled autocomplete="off">
            </div>
          </div>

          <div class="field">
            <label class="label">{{ $t('description') }}</label>
            <div class="control">
              <input v-model="versionItem.description" class="input" type="text" autocomplete="off">
            </div>
          </div>

          <div class="field">
            <label class="label">{{ $t('forwarders.stableVersion') }}</label>
            <input id="is_stable" v-model="versionItem.is_stable" type="checkbox" name="is_stable" class="switch"
              :disabled="originalIsStable">
            <label for="is_stable">{{ versionItem.is_stable ? $t('yes') : $t('no') }}</label>
            <p v-if="originalIsStable" class="help">{{ $t('forwarders.stableVersionHelp') }}</p>
          </div>

          <div class="modal-footer">
            <button type="button" class="button cancel-button" @click="versionModal = false; isUpdatingVersion = false">
              {{ $t('cancel') }}
            </button>
            <button type="submit" class="button is-primary" :disabled="isUpdatingVersion" :class="{ 'is-loading': isUpdatingVersion }">
              {{ $t('save') }}
            </button>
          </div>
        </form>
      </div>
    </modal>
  </section>
</template>
<script>
import clipboard from '@/clipboard.js'
import { DataColumn, DataTable, Modal } from '@cyradar/ui'
import { format } from 'date-fns'
import * as icons from 'octicons-vue'
import { v4 as uuid } from 'uuid'
import SvgIcons from '@/components/common/SvgIcons.vue'
import AgentDownloadDropdown from '@/components/forwarders/AgentDownloadDropdown.vue'
import FileUploadSection from '@/components/common/FileUploadSection.vue'
import ForwarderList from '@/components/forwarders/ForwarderList.vue'
import AgentList from '@/components/forwarders/AgentList.vue'
import ForwarderFilter from '@/components/forwarders/ForwarderFilter.vue'
import AddForwarderModal from '@/components/forwarders/AddForwarderModal.vue'
import { nameMap, ensureForwarderStructure, FILE_UPLOAD_CONFIG, createInitialDragStates } from '@/utils/forwarders'



export default {
  components: {
    DataTable,
    DataColumn,
    Modal,
    SvgIcons,
    AgentDownloadDropdown,
    FileUploadSection,
    ForwarderList,
    AgentList,
    ForwarderFilter,
    AddForwarderModal
  },
  data() {
    return {
      createVersionModal: false,
      versionModal: false,
      description: '',
      isSavingVersion: false,
      isUpdatingVersion: false,
      versionItem: null,
      originalIsStable: false,
      newOption: '',
      showAddForwarderModal: false,
      isEditMode: false,
      editItem: null,
      item: null,
      source_types: [],
      indexes: [],
      selectedFiles: {},
      dragStates: createInitialDragStates(),
      showForwarderFilter: false,
      forwarderFilters: {
        statuses: [],
        modules: []
      },
      actualFilteredCount: 0
    }
  },
  computed: {
    icons() {
      return icons
    },
    sourceTypeNames() {
      return nameMap(this.source_types)
    },
    indexNames() {
      return nameMap(this.indexes)
    },
    windowsSourceTypes() {
      return this.source_types.filter(e => e.type === 'xml');
    },
    linuxSourceTypes() {
      return this.source_types.filter(e => e.type === 'linux_syslog')
    },
    resourceMonitorSourceTypes() {
      return this.source_types.filter(e => e.type === 'json');
    },
    fileLogSourceTypes() {
      return this.source_types;
    },
    hasActiveFilters() {
      return this.actualFilteredCount > 0
    },
    activeFilterCount() {
      return this.actualFilteredCount
    }
  },
  watch: {
    item(val) {
      if (val && val.token) return
      this.randomToken()
    }
  },
  mounted() {
    this.loadIndexes()
    this.loadSourceTypes()

    // Prevent password manager popup by adding hidden dummy inputs
    this.$nextTick(() => {
      const searchInput = this.$el.querySelector('input[type="search"], input[placeholder*="Search"], input[placeholder*="Tìm kiếm"]')
      if (searchInput && searchInput.parentElement) {
        // Create hidden dummy inputs to mislead password managers
        const dummyUsername = document.createElement('input')
        dummyUsername.setAttribute('type', 'text')
        dummyUsername.setAttribute('name', 'fake_username_' + Math.random())
        dummyUsername.style.position = 'absolute'
        dummyUsername.style.top = '-9999px'
        dummyUsername.style.left = '-9999px'
        dummyUsername.setAttribute('tabindex', '-1')
        dummyUsername.setAttribute('aria-hidden', 'true')

        const dummyPassword = document.createElement('input')
        dummyPassword.setAttribute('type', 'password')
        dummyPassword.setAttribute('name', 'fake_password_' + Math.random())
        dummyPassword.style.position = 'absolute'
        dummyPassword.style.top = '-9999px'
        dummyPassword.style.left = '-9999px'
        dummyPassword.setAttribute('tabindex', '-1')
        dummyPassword.setAttribute('aria-hidden', 'true')

        // Insert dummy inputs before the search input
        searchInput.parentElement.insertBefore(dummyUsername, searchInput)
        searchInput.parentElement.insertBefore(dummyPassword, searchInput)
      }
    })
  },
  methods: {
    format,
    copyToken() {
      clipboard(this.item.token || '')
    },
    randomToken() {
      const token = uuid()
      if (!token) return
      this.$set(this.item, 'token', token.replace(/-/g, ''))
    },
    download(os, bit) {
      window.location.assign('/forwarder/installer?os=' + os + '&bit=' + bit)
    },
    downloadFromUrl(url) {
      window.location.assign(url);
    },
    handleUploadedAgents() {
      this.isSavingVersion = true
      const formData = new FormData()
      if (this.description) formData.append('description', this.description)

      let atLeastOneFileSelected = false
      FILE_UPLOAD_CONFIG.fileTypes.forEach(type => {
        const fileInput = document.querySelector(`input[name="${type}"]`)
        if (fileInput && fileInput.files && fileInput.files[0]) {
          const file = fileInput.files[0]
          const fieldName = FILE_UPLOAD_CONFIG.fileFieldMap[type]
          formData.append(fieldName, file)
          formData.append(`os`, FILE_UPLOAD_CONFIG.fileInfo[type].os)
          formData.append(`architecture`, FILE_UPLOAD_CONFIG.fileInfo[type].architecture)
          atLeastOneFileSelected = true
        }
      })
      if (!atLeastOneFileSelected) {
        this.$store.dispatch('NOTIFY', { type: 'error', text: this.$t('forwarders.pleaseUploadAtLeastOneFile') })
        this.isSavingVersion = false
        return
      }
      this.$http.post(`/api/v1/agent/version`, formData)
        .then((body) => {
          if (!body || !body.data) return
          this.resetForm()
          this.$store.dispatch('NOTIFY', { type: 'success', text: body.data.message })
          this.isSavingVersion = false
          this.createVersionModal = false
        })
        .catch((e) => {
          this.$store.dispatch('NOTIFY', { type: 'error', text: e.response?.data?.message || this.$t('forwarders.cannotUploadNewVersion') })
          this.isSavingVersion = false
        })
    },
    async create() {
      // Ensure indexes and source types are loaded before opening modal
      if (this.indexes.length === 0) {
        await this.loadIndexes()
      }
      if (this.source_types.length === 0) {
        await this.loadSourceTypes()
      }
      this.isEditMode = false
      this.editItem = null
      this.showAddForwarderModal = true
    },
    handleForwarderSuccess() {
      this.showAddForwarderModal = false
      this.isEditMode = false
      this.editItem = null
      if (this.$refs.forwarderList) {
        this.$refs.forwarderList.loadForwarders()
      }
    },
    async edit(item) {
      this.item = ensureForwarderStructure(item)
      
      // Ensure both indexes and source types are loaded
      const promises = []
      if (this.indexes.length === 0) {
        promises.push(this.loadIndexes())
      }
      if (this.source_types.length === 0) {
        promises.push(this.loadSourceTypes())
      }
      
      await Promise.all(promises)
      
      // Set default source types if needed
      if (this.item.resource_monitor && (!this.item.resource_monitor.source_type_id || !this.resourceMonitorSourceTypes.some(e => e.id === this.item.resource_monitor.source_type_id)) && this.resourceMonitorSourceTypes.length > 0) {
        this.$set(this.item.resource_monitor, 'source_type_id', this.resourceMonitorSourceTypes[0].id);
      }
      if (this.item.system_log && this.item.system_log.windows && (!this.item.system_log.windows.source_type_id || !this.windowsSourceTypes.some(e => e.id === this.item.system_log.windows.source_type_id)) && this.windowsSourceTypes.length > 0) {
        this.$set(this.item.system_log.windows, 'source_type_id', this.windowsSourceTypes[0].id);
      }
      if (this.item.system_log && this.item.system_log.linux && (!this.item.system_log.linux.source_type_id || !this.linuxSourceTypes.some(e => e.id === this.item.system_log.linux.source_type_id)) && this.linuxSourceTypes.length > 0) {
        this.$set(this.item.system_log.linux, 'source_type_id', this.linuxSourceTypes[0].id);
      }
      // Note: Removed auto-assignment of default index_id values to maintain
      // consistency with new stream creation behavior (streams should start with empty index_id)
      
      // Use AddForwarderModal for editing
      this.isEditMode = true
      this.editItem = this.item
      this.showAddForwarderModal = true
    },
    loadSourceTypes() {
      return this.$http.get('/api/v1/source-types/names').then((body) => {
        if (!body || !body.data || !body.data.data) return
        this.source_types = body.data.data
      })
    },
    loadIndexes() {
      return this.$http.get('/api/v1/indexes/names').then((body) => {
        if (!body || !body.data || !body.data.data) return
        this.indexes = body.data.data
      })
    },
    items(filter, order, pagination) {
      return this.$http.get('/api/v1/forwarders', {
        params: {
          filter: filter.query,
          sort: order.by,
          order: order.order,
          page: pagination.page,
          limit: pagination.perPage
        }
      }).then((body) => {
        if (!body || !body.data || !body.data.data) return { items: [], total: 0 }
        const result = body.data.data
        // Ensure we have the correct structure
        if (result && typeof result === 'object' && Array.isArray(result.items)) {
          return {
            items: result.items,
            total: result.total || 0
          }
        }
        // Fallback if structure is different
        return { items: [], total: 0 }
      })
    },
    fetchVersions(filter, order, pagination) {
      return this.$http.get('/api/v1/agent/version', {
        params: {
          filter: filter.query,
          sort: order.by,
          order: order.order,
          page: pagination.page,
          limit: pagination.perPage
        }
      }).then((body) => {
        if (!body || !body.data || !body.data.data) return { items: [], total: 0 }
        const result = body.data.data
        // Ensure we have the correct structure
        if (result && typeof result === 'object' && Array.isArray(result.items)) {
          return {
            items: result.items,
            total: result.total || 0
          }
        }
        // Fallback if structure is different
        return { items: [], total: 0 }
      })
    },
    editVersion(item) {
      this.versionItem = JSON.parse(JSON.stringify(item));
      this.originalIsStable = item.is_stable;
      this.versionModal = true;
    },
    updateVersion() {
      this.isUpdatingVersion = true;
      const payload = { description: this.versionItem.description };
      if (this.versionItem.is_stable && !this.originalIsStable) payload.is_stable = true;
      this.$http.patch(`/api/v1/agent/version/${this.versionItem.id}`, payload)
        .then((body) => {
          if (!body || !body.data) return;
          this.versionModal = false;
          this.isUpdatingVersion = false;
          this.$store.dispatch('NOTIFY', { type: 'success', text: body.data.message || this.$t('forwarders.versionUpdatedSuccessfully') });
          this.$refs.versionTable.loadItems();
        })
        .catch((e) => {
          this.$store.dispatch('NOTIFY', { type: 'error', text: e.response?.data?.message || this.$t('forwarders.failedToUpdateVersion') });
          this.isUpdatingVersion = false;
        });
    },
    handleFileSelected(event, os) {
      const file = event.target.files[0];
      if (file) {
        const mappedOs = os;
        this.$set(this.selectedFiles, mappedOs, { name: file.name, size: file.size, type: file.type });
      }
    },
    handleDragOver(event) {
      event.preventDefault();
      event.dataTransfer.dropEffect = 'copy';
    },
    handleDragEnter(event, os) {
      event.preventDefault();
      this.$set(this.dragStates, os, true);
    },
    handleDragLeave(event, os) {
      event.preventDefault();
      // Only set drag state to false if we're leaving the container itself, not a child element
      if (!event.currentTarget.contains(event.relatedTarget)) {
        this.$set(this.dragStates, os, false);
      }
    },
    handleDrop(event, os) {
      event.preventDefault();
      this.$set(this.dragStates, os, false);

      const files = event.dataTransfer.files;
      if (files && files.length > 0) {
        const file = files[0];
        // Validate file type (optional - you can add specific validation here)
        if (file) {
          this.$set(this.selectedFiles, os, { name: file.name, size: file.size, type: file.type });

          // Also update the corresponding file input element
          const fileInput = document.querySelector(`input[name="${os}"]`);
          if (fileInput) {
            // Create a new FileList-like object and assign it to the input
            const dataTransfer = new DataTransfer();
            dataTransfer.items.add(file);
            fileInput.files = dataTransfer.files;
          }
        }
      }
    },
    resetForm() {
      this.selectedFiles = {}
      this.description = ''
      this.dragStates = createInitialDragStates()
    },
    isFormValid() {
      return Object.keys(this.selectedFiles).length > 0;
    },
    handleShowFilter() {
      this.showForwarderFilter = true
    },
    handleApplyFilters(filters) {
      this.forwarderFilters = {
        statuses: [...filters.statuses],
        modules: [...filters.modules]
      }

      // Use the result count from the filter modal
      this.actualFilteredCount = filters.resultCount || 0
    }
  }
}
</script>
<style lang="scss">
@import '@/assets/styles/dark-modal.scss';

.ml-1 {
  margin-left: 0.25rem;
}

.ml-2 {
  margin-left: 0.5rem;
}

.forwarders-download {
  svg {
    margin: 0 5px -3px 0;
    fill: #363636;
    width: 16px;

    .dashboard.is-dark & {
      fill: white;
    }
  }
}

/* New version upload form styles */
.os-upload-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
  margin-bottom: 1.5rem;
  width: 100%;
  max-width: 95%;
}

.upload-section {
  background-color: #40424a;
  border-radius: 8px;
  padding: 0.75rem;
  height: calc(1.5 * (130px)); /* Increase height by 1.5x */
  display: flex;
  flex-direction: column;

  h3 {
    color: #fff;
    font-size: 1.1rem;
    margin-bottom: 0.75rem;
  }
}

.file-upload {
  margin-bottom: 0.75rem;
  flex-grow: 1;
  display: flex;
  flex-direction: column;

  .file-upload-container {
    display: flex;
    align-items: center;
    border: 1px dashed rgba(255, 255, 255, 0.3);
    border-radius: 8px;
    padding: 0.5rem;
    background-color: #2a2d36;
    transition: all 0.3s ease;

    &.drag-over {
      border-color: #4A69FF;
      background-color: rgba(74, 105, 255, 0.1);
      border-width: 2px;
      transform: scale(1.02);
      box-shadow: 0 4px 12px rgba(74, 105, 255, 0.3);
    }
  }

  .file-upload-button {
    display: inline-flex;
    align-items: center;
    cursor: pointer;

    &.outlined {
      border: 1px solid rgba(255, 255, 255, 0.3);
      border-radius: 4px;
      padding: 0.25rem 0.5rem;;
      color: #fff;
      background-color: #1b1c3c;

      .icon {
        margin-right: 0.5rem;

        svg {
          fill: currentColor;
          width: 18px;
          height: 18px;
        }
      }
    }
  }

  .or-text {
    color: rgba(255, 255, 255, 0.6);
    margin-left: 0.75rem;
    font-size: 0.85rem;
  }

  .hidden-input {
    display: none;
  }
}

.file-selected {
  display: flex;
  align-items: center;
  background-color: rgba(255, 255, 255, 0.05);
  padding: 0.5rem;
  border-radius: 8px;

  .file-icon {
    margin-right: 0.75rem;
    color: #FFC107;

    svg {
      fill: currentColor;
      width: 20px;
      height: 20px;
    }
  }

  .file-info {
    flex: 1;

    .file-name {
      color: #fff;
      margin-bottom: 0.25rem;
      border-color: transparent;
      border-style: solid;
      border-width: 0px 0px 0px 0px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: 100%;
    }

    .file-status {
      color: #4CAF50;
      font-size: 0.8rem;
      display: flex;
      align-items: center;

      .success-icon {
        margin-right: 0.25rem;
        display: flex;
        align-items: center;

        svg {
          fill: currentColor;
          width: 14px;
          height: 14px;
        }
      }
    }
  }
}

.form-fields-container {
  display: flex;
  gap: 1.5rem;
  margin-bottom: 1.5rem;
  width: 100%;
  max-width: 95%;
}

.form-column {
  flex: 1;

  .field {
    margin-bottom: 1rem;
  }

  .description-field {
    height: 100%;

    .control {
      height: 100%;
      display: flex;
      flex-direction: column;

      .input {
        background-color: #2a2d36 !important;
        color: #fff;
      }

      .textarea {
        flex: 1;
        min-height: 80px;
        background-color: #2a2d36;
        color: #fff;
        border: 1px solid;
        border-color: #404553;
      }
    }
  }

  .label {
    color: #fff;
    margin-bottom: 0.5rem;
    display: block;

    .required-mark {
      font-weight: bold;
      margin-left: 2px;
    }
  }

  .char-count {
    text-align: right;
    color: rgba(255, 255, 255, 0.5);
    font-size: 0.8rem;
    margin-top: 0.25rem;
  }

  .control {
    input {
      background-color: #2a2d36;
      color: #fff;
      border: none;
      width: 100%;
    }
  }
}

.file-format-info {
  background-color: #2a2d36;
  padding: 0.75rem 1rem;
  border-radius: 4px;
  color: rgba(255, 255, 255, 0.7);
  display: flex;
  justify-content: flex-start;
  align-items: center;
  width: auto;
  flex: 1;
  margin-right: 1rem;

  .info-icon {
    color: #4A69FF;
    display: flex;
    align-items: center;

    svg {
      fill: currentColor;
      width: 18px;
      height: 18px;
    }
  }
}

.form-footer-container {
  display: flex;
  align-items: center;
  width: 100%;
  max-width: 95%;
  margin-bottom: 1.5rem;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;

  .cancel-button {
    background-color: transparent;
    border: 1px solid rgba(255, 255, 255, 0.3);
    color: #fff;
  }

  .button.is-primary {
    background-color: #1F6FF4 !important;
    border: none;
  }
}

// Modal content width override for forwarder modals
.modal .modal-content {
  width: 45% !important;
}

// Additional styles for switch in modals
.dark-modal {
  .switch {
    & + label {
      color: $modal-text;
    }
  }
}

/* Enhanced v-select dropdown styling for better visual separation */
.v-select, .v-select .vs__dropdown-menu, .v-select .vs__dropdown-option, .v-select .vs__selected {
  background-color: transparent !important;
  color: #fff !important;
  border-color: #404553 !important;
}

.v-select .vs__dropdown-menu {
  background-color: #2d3d80 !important;
  border: 1px solid #404553 !important;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3) !important;
  border-radius: 4px !important;
  max-height: 300px !important;
  overflow-y: auto !important;
}

/* Individual dropdown options with visual separation */
.v-select .vs__dropdown-option {
  background-color: #2d3d80 !important;
  color: #fff !important;
  padding: 8px 12px !important;
  border-bottom: 1px solid rgba(64, 69, 83, 0.5) !important;
  transition: all 0.2s ease !important;
  cursor: pointer !important;
}

/* Remove border from last option */
.v-select .vs__dropdown-option:last-child {
  border-bottom: none !important;
}

/* Hover effect for better user interaction */
.v-select .vs__dropdown-option:hover,
.v-select .vs__dropdown-option--highlight {
  background-color: #3d4d90 !important;
  color: #fff !important;
  transform: translateX(2px) !important;
}

/* Active/focused option styling */
.v-select .vs__dropdown-option--highlight {
  background-color: #4A69FF !important;
  color: #fff !important;
  box-shadow: inset 3px 0 0 #1F6FF4 !important;
}

/* Selected option styling with distinct appearance */
.v-select .vs__dropdown-option[aria-selected='true'],
.v-select .vs__dropdown-option.vs__dropdown-option--selected {
  background-color: #1F6FF4 !important;
  color: #fff !important;
  font-weight: 500 !important;
  position: relative !important;
}

/* Add checkmark icon for selected options */
.v-select .vs__dropdown-option[aria-selected='true']::after,
.v-select .vs__dropdown-option.vs__dropdown-option--selected::after {
  content: '✓' !important;
  position: absolute !important;
  right: 12px !important;
  top: 50% !important;
  transform: translateY(-50%) !important;
  color: #fff !important;
  font-weight: bold !important;
}

.v-select .vs__dropdown-toggle {
  color: #fff !important;
  padding: 4px 8px !important;
  border: 1px solid #404553 !important;
  border-radius: 4px !important;
}

.v-select .vs__search, .v-select .vs__search:focus {
  background-color: transparent !important;
  color: #fff !important;
}

.v-select .vs__actions {
  color: #fff !important;
}

/* Selected tags styling */
.v-select .vs__selected {
  background-color: #1F6FF4 !important;
  border-color: #404553 !important;
  color: #fff !important;
  padding: 2px 8px !important;
  margin: 2px 4px 2px 0 !important;
  border-radius: 3px !important;
  font-size: 0.85rem !important;
}

.v-select .vs__deselect {
  fill: #fff !important;
}

/* Improved no-options message */
.v-select .vs__no-options {
  background-color: #2d3d80 !important;
  color: rgba(255, 255, 255, 0.7) !important;
  padding: 12px !important;
  text-align: center !important;
  font-style: italic !important;
}

/* Loading state styling */
.v-select .vs__spinner {
  border-color: rgba(255, 255, 255, 0.3) !important;
  border-top-color: #4A69FF !important;
}
</style>
